import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button';
import EmptyState from 'components/EmptyState/EmptyState';
import Grid from 'components/Grid';
import TextLoader from 'components/TextLoader';
import HomeWidget from './HomeWidget';
import useHomeLayout from './useHomeLayout';

// Widgets decide for themselves whether to render, some only once their content has
// loaded, so the grid is watched rather than the layout counted
const useIsEmpty = (ref, active) => {
  const [isEmpty, setIsEmpty] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;

    if (!active || !element) {
      return undefined;
    }

    const check = () => setIsEmpty(!element.querySelector(':scope > :not([data-home-empty]):not(:empty)'));
    const observer = new MutationObserver(check);

    check();
    observer.observe(element, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [ref, active]);

  return isEmpty;
};

const HomeWidgets = ({ spacing }) => {
  const { t } = useTranslation();

  const { widgets, loading, failed, retry } = useHomeLayout();

  const gridRef = useRef();
  const isEmpty = useIsEmpty(gridRef, !loading);

  // Widgets fetch their own content on mount, so none may render before the
  // layout says which ones the member gets
  if (loading) {
    return <TextLoader delay={500} />;
  }

  return (
    <Grid container spacing={spacing} direction="column" wrap="nowrap" ref={gridRef}>
      {/* The backend's id tells widgets of the same type apart; the client's own widgets have one of each type */}
      {widgets.map(({ id, type, config }) => <HomeWidget key={id ?? type} type={type} config={config} />)}
      {failed && (
        <Grid item>
          <EmptyState title={t('homeFailed.title')} subtitle={<Button onClick={retry}>{t('homeFailed.retry')}</Button>} />
        </Grid>
      )}
      {isEmpty && (
        <Grid item data-home-empty>
          <EmptyState title={t('homeEmpty.title')} subtitle={t('homeEmpty.subtitle')} />
        </Grid>
      )}
    </Grid>
  );
};

HomeWidgets.propTypes = {
  spacing: PropTypes.number.isRequired,
};

export default HomeWidgets;
